import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type {
  Criticality,
  EvidenceCoverageRequirement,
  LifecycleState,
  RiskTier,
  VendorClassification,
  VendorEvidence,
  VendorEvidenceVerification,
  VendorLifecycleTransition,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import {
  CRITICALITIES,
  CRITICALITY_LABEL,
  LIFECYCLE_STATES,
  RISK_TIERS,
  STATE_LABEL,
  TIER_LABEL,
  VALIDITY_LABEL,
  stateTone,
  tierTone,
  validityTone,
} from "./labels";

const OUTCOME_HEADLINE: Record<string, string> = {
  MATCH: "The document is the one this evidence relied on.",
  MISMATCH: "The document has changed since this evidence was attached.",
  UNAVAILABLE: "The document cannot be read, so nothing could be compared.",
};

const COVERAGE_TONE: Record<string, "success" | "warning" | "danger"> = {
  SATISFIED: "success",
  EXPIRING: "warning",
  EXPIRED: "danger",
  MISSING: "danger",
};

interface Member {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

function message(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  if (err.code === "forbidden" && typeof err.details?.permission === "string") {
    return `You do not hold ${err.details.permission}, so this action is not yours to take.`;
  }
  return err.message;
}

export function VenVendorDetail() {
  const { vendorId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [action, setAction] = useState<
    null | "classify" | "intake" | "transition" | "evidence" | "owner"
  >(null);
  // A verification outcome outlives the refresh it triggers: the reload blanks
  // the page while it runs, and a result the reader never saw is not a result.
  const [outcome, setOutcome] = useState<VendorEvidenceVerification | null>(null);

  const state = useAsync(
    async () => {
      const [vendor, evidence, coverage, transitions, classifications] = await Promise.all([
        vendorApi.vendor(orgId, vendorId),
        vendorApi.vendorEvidence(orgId, vendorId),
        vendorApi.coverage(orgId, vendorId),
        vendorApi.transitions(orgId, vendorId),
        vendorApi.classifications(orgId, vendorId),
      ]);
      return { vendor, evidence, coverage, transitions, classifications };
    },
    [orgId, vendorId],
  );

  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const personName = (id: string | null | undefined): string => {
    if (!id) return "Unassigned";
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const vendor = state.data?.vendor.data.vendor;
  const evidence = state.data?.evidence.data.evidence ?? [];
  const coverage = state.data?.coverage.data.coverage;
  const transitions = state.data?.transitions.data.transitions ?? [];
  const classifications = state.data?.classifications.data.classifications ?? [];
  const demoMode = state.data?.vendor.demoMode ?? false;
  const reload = state.reload;

  if (state.loading && !state.data) return <LoadingState label="Loading this vendor…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={reload} />;
  if (!vendor) return <EmptyState title="Vendor not found" description="It may have been removed." />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{vendor.legalName}</h1>
          <p>
            {vendor.categoryLabel}
            {vendor.website ? ` · ${vendor.website}` : ""}
          </p>
        </div>
        <div className="ods-row">
          <Link to="/studio/vendor-ai/vendors">
            <Button size="sm" variant="secondary">
              Back to register
            </Button>
          </Link>
        </div>
      </div>

      <VendorDemoBanner show={demoMode} />

      <Card>
        <CardHeader
          title="Standing"
          action={
            <div className="ods-row">
              <Button size="sm" variant="secondary" onClick={() => setAction("owner")}>
                {vendor.owner ? "Change owner" : "Assign owner"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAction("classify")}>
                Reclassify
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAction("transition")}>
                Change state
              </Button>
              <Button size="sm" onClick={() => setAction("intake")}>
                Request use
              </Button>
            </div>
          }
        />
        <CardBody>
          <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
            <Badge tone={tierTone(vendor.riskTier)}>{TIER_LABEL[vendor.riskTier]}</Badge>
            <Badge tone="neutral">{CRITICALITY_LABEL[vendor.criticality]}</Badge>
            <Badge tone={stateTone(vendor.lifecycleState)}>{STATE_LABEL[vendor.lifecycleState]}</Badge>
            {vendor.dataClassifications.map((d) => (
              <Badge key={d} tone="info">
                {d}
              </Badge>
            ))}
          </div>
          <p style={{ marginTop: "var(--ods-space-3)" }}>
            {`Owner: ${personName(vendor.owner?.ownerUserId)}${
              vendor.owner?.ownerRole ? ` (${vendor.owner.ownerRole})` : ""
            }${
              vendor.countriesOfOperation.length > 0
                ? ` · Operates in ${vendor.countriesOfOperation.join(", ")}`
                : ""
            }`}
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Evidence coverage"
          action={
            <Button size="sm" onClick={() => setAction("evidence")}>
              Attach evidence
            </Button>
          }
        />
        <CardBody>
          {!coverage || coverage.required.length === 0 ? (
            <EmptyState title="No requirements" description="This tier does not require standing evidence." />
          ) : (
            <DataTable
              columns={[
                {
                  key: "evidenceTypeLabel",
                  header: "Required",
                  render: (r: EvidenceCoverageRequirement) => r.evidenceTypeLabel,
                },
                {
                  key: "status",
                  header: "Status",
                  render: (r: EvidenceCoverageRequirement) => (
                    <Badge tone={COVERAGE_TONE[r.status] ?? "neutral"}>{r.status}</Badge>
                  ),
                },
                {
                  key: "expiresAt",
                  header: "Expires",
                  render: (r: EvidenceCoverageRequirement) => (r.expiresAt ? formatDate(r.expiresAt) : "—"),
                },
              ]}
              rows={coverage.required.map((r) => ({ ...r, id: r.evidenceType }))}
              caption="Evidence coverage"
            />
          )}
        </CardBody>
      </Card>

      <EvidenceList
        orgId={orgId}
        evidence={evidence}
        outcome={outcome}
        onOutcome={setOutcome}
        onChanged={reload}
      />

      <Card>
        <CardHeader title="Classification history" />
        <CardBody>
          {classifications.length === 0 ? (
            <EmptyState title="No classifications yet" description="This vendor has not been classified." />
          ) : (
            <DataTable
              columns={[
                { key: "revision", header: "Rev", render: (c: VendorClassification) => `#${c.revision}` },
                {
                  key: "tier",
                  header: "Tier and criticality",
                  render: (c: VendorClassification) => (
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                      <Badge tone={tierTone(c.riskTier)}>{TIER_LABEL[c.riskTier] ?? c.riskTier}</Badge>
                      <Badge tone="neutral">{CRITICALITY_LABEL[c.criticality] ?? c.criticality}</Badge>
                    </div>
                  ),
                },
                { key: "rationale", header: "Why", render: (c: VendorClassification) => c.rationale },
                { key: "changedBy", header: "Who", render: (c: VendorClassification) => personName(c.changedBy) },
                { key: "createdAt", header: "When", render: (c: VendorClassification) => formatDate(c.createdAt) },
              ]}
              rows={classifications}
              caption="Classification history"
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Lifecycle history" />
        <CardBody>
          {transitions.length === 0 ? (
            <EmptyState title="No transitions yet" description="This vendor has not moved state." />
          ) : (
            <DataTable
              columns={[
                {
                  key: "move",
                  header: "Move",
                  render: (t: VendorLifecycleTransition) =>
                    `${STATE_LABEL[t.fromState] ?? t.fromState} → ${STATE_LABEL[t.toState] ?? t.toState}`,
                },
                { key: "reason", header: "Reason", render: (t: VendorLifecycleTransition) => t.reason },
                { key: "createdAt", header: "When", render: (t: VendorLifecycleTransition) => formatDate(t.createdAt) },
              ]}
              rows={transitions}
              caption="Lifecycle transitions"
            />
          )}
        </CardBody>
      </Card>

      <AssignOwnerDialog
        open={action === "owner"}
        orgId={orgId}
        vendorId={vendorId}
        members={members.data ?? []}
        current={vendor.owner?.ownerUserId ?? null}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          reload();
        }}
      />
      <ClassifyDialog
        open={action === "classify"}
        orgId={orgId}
        vendorId={vendorId}
        current={{ riskTier: vendor.riskTier, criticality: vendor.criticality }}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          reload();
        }}
      />
      <IntakeDialog
        open={action === "intake"}
        orgId={orgId}
        vendorId={vendorId}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          reload();
        }}
      />
      <TransitionDialog
        open={action === "transition"}
        orgId={orgId}
        vendorId={vendorId}
        current={vendor.lifecycleState}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          reload();
        }}
      />
      <AttachEvidenceDialog
        open={action === "evidence"}
        orgId={orgId}
        vendorId={vendorId}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          reload();
        }}
      />
    </>
  );
}

function EvidenceList({
  orgId,
  evidence,
  outcome,
  onOutcome,
  onChanged,
}: {
  orgId: string;
  evidence: VendorEvidence[];
  outcome: VendorEvidenceVerification | null;
  onOutcome: (outcome: VendorEvidenceVerification) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function verify(id: string) {
    setBusy(id);
    setError(null);
    try {
      const res = await vendorApi.verifyEvidence(orgId, id);
      onOutcome(res.data.outcome);
      onChanged();
    } catch (err) {
      setError(message(err, "Could not verify this evidence."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader title="Evidence" />
      <CardBody>
        {error && <ErrorState description={error} />}
        {outcome && (
          <div
            className="ods-failclosed"
            role="status"
            style={{
              borderColor:
                outcome.outcome === "MATCH" ? "var(--ods-color-success)" : "var(--ods-color-warning)",
            }}
          >
            <strong>{OUTCOME_HEADLINE[outcome.outcome] ?? outcome.outcome}</strong> {outcome.detail}
            {outcome.outcome === "MISMATCH" && (
              <>
                {" "}
                The evidence still records the checksum it relied on; this finding sits alongside it rather than
                replacing it.
              </>
            )}
          </div>
        )}
        {evidence.length === 0 ? (
          <EmptyState title="No evidence attached" description="Attach a document this vendor's assurance rests on." />
        ) : (
          <DataTable
            columns={[
              { key: "evidenceTypeLabel", header: "Type", render: (e: VendorEvidence) => e.evidenceTypeLabel },
              { key: "documentFilename", header: "Document", render: (e: VendorEvidence) => e.documentFilename },
              {
                key: "validity",
                header: "Validity",
                render: (e: VendorEvidence) => (
                  <Badge tone={validityTone(e.validity)}>{VALIDITY_LABEL[e.validity] ?? e.validity}</Badge>
                ),
              },
              {
                key: "expiresAt",
                header: "Expires",
                render: (e: VendorEvidence) => (e.expiresAt ? formatDate(e.expiresAt) : "No expiry"),
              },
              {
                key: "lastVerification",
                header: "Last check",
                render: (e: VendorEvidence) =>
                  e.lastVerification ? (
                    <Badge tone={e.lastVerification === "MATCH" ? "success" : "warning"}>{e.lastVerification}</Badge>
                  ) : (
                    "Never"
                  ),
              },
              {
                key: "verify",
                header: "",
                render: (e: VendorEvidence) => (
                  <Button size="sm" variant="secondary" onClick={() => verify(e.id)} disabled={busy === e.id}>
                    {busy === e.id ? "Checking…" : "Verify"}
                  </Button>
                ),
              },
            ]}
            rows={evidence}
            caption="Vendor evidence"
          />
        )}
      </CardBody>
    </Card>
  );
}

function ActionDialog({
  open,
  title,
  description,
  confirmLabel,
  disabled,
  onClose,
  run,
  onDone,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  disabled?: boolean;
  onClose: () => void;
  run: () => Promise<unknown>;
  onDone: () => void;
  children: React.ReactNode;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await run();
      onDone();
    } catch (err) {
      setError(message(err, "That did not go through."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title={title}
      description={description}
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || disabled}>
            {saving ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      {error && <ErrorState title="Not recorded" description={error} />}
      {children}
    </Dialog>
  );
}

function AssignOwnerDialog({
  open,
  orgId,
  vendorId,
  members,
  current,
  onClose,
  onDone,
}: {
  open: boolean;
  orgId: string;
  vendorId: string;
  members: Member[];
  current: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [ownerUserId, setOwnerUserId] = useState(current ?? "");
  const [ownerRole, setOwnerRole] = useState("Vendor manager");

  return (
    <ActionDialog
      open={open}
      title="Assign this vendor an owner"
      description="An owner is a person in this organization, not a name typed into a box."
      confirmLabel="Save owner"
      disabled={ownerUserId === "" || ownerUserId === current}
      onClose={onClose}
      onDone={onDone}
      run={() => vendorApi.assignOwner(orgId, vendorId, ownerUserId, ownerRole.trim() || undefined)}
    >
      {members.length === 0 ? (
        <ErrorState
          title="No members to choose from"
          description="This organization has no other members loaded, so ownership cannot be assigned here yet."
        />
      ) : (
        <div className="form-grid">
          <Field label="Owner">
            {(id) => (
              <Select id={id} value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
                <option value="">Choose a person</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName || m.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Their role for this vendor">
            {(id) => <TextInput id={id} value={ownerRole} onChange={(e) => setOwnerRole(e.target.value)} />}
          </Field>
        </div>
      )}
    </ActionDialog>
  );
}

function ClassifyDialog({
  open,
  orgId,
  vendorId,
  current,
  onClose,
  onDone,
}: {
  open: boolean;
  orgId: string;
  vendorId: string;
  current: { riskTier: RiskTier; criticality: Criticality };
  onClose: () => void;
  onDone: () => void;
}) {
  const [riskTier, setRiskTier] = useState<RiskTier>(current.riskTier);
  const [criticality, setCriticality] = useState<Criticality>(current.criticality);
  const [rationale, setRationale] = useState("");

  return (
    <ActionDialog
      open={open}
      title="Reclassify this vendor"
      description="Each classification is kept with its reason, so how the tier moved stays readable later."
      confirmLabel="Save classification"
      disabled={rationale.trim() === ""}
      onClose={onClose}
      onDone={onDone}
      run={() => vendorApi.classify(orgId, vendorId, { riskTier, criticality, rationale: rationale.trim() })}
    >
      <div className="form-grid">
        <Field label="Risk tier">
          {(id) => (
            <Select id={id} value={riskTier} onChange={(e) => setRiskTier(e.target.value as RiskTier)}>
              {RISK_TIERS.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Criticality">
          {(id) => (
            <Select id={id} value={criticality} onChange={(e) => setCriticality(e.target.value as Criticality)}>
              {CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {CRITICALITY_LABEL[c]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <Field label="Rationale">
        {(id) => <TextArea id={id} rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} />}
      </Field>
    </ActionDialog>
  );
}

function IntakeDialog({
  open,
  orgId,
  vendorId,
  onClose,
  onDone,
}: {
  open: boolean;
  orgId: string;
  vendorId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [businessJustification, setJustification] = useState("");
  const [intendedUse, setIntendedUse] = useState("");
  const [dataShared, setDataShared] = useState("");

  return (
    <ActionDialog
      open={open}
      title="Request to use this vendor"
      description="Someone other than you decides this request."
      confirmLabel="Submit request"
      disabled={businessJustification.trim() === "" || intendedUse.trim() === ""}
      onClose={onClose}
      onDone={onDone}
      run={() =>
        vendorApi.submitIntake(orgId, vendorId, {
          businessJustification: businessJustification.trim(),
          intendedUse: intendedUse.trim(),
          dataShared: dataShared
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        })
      }
    >
      <Field label="Business justification">
        {(id) => (
          <TextArea id={id} rows={3} value={businessJustification} onChange={(e) => setJustification(e.target.value)} />
        )}
      </Field>
      <Field label="Intended use">
        {(id) => <TextInput id={id} value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} />}
      </Field>
      <Field label="Data shared" help="Comma separated, for example: CUI, PII">
        {(id) => <TextInput id={id} value={dataShared} onChange={(e) => setDataShared(e.target.value)} />}
      </Field>
    </ActionDialog>
  );
}

function TransitionDialog({
  open,
  orgId,
  vendorId,
  current,
  onClose,
  onDone,
}: {
  open: boolean;
  orgId: string;
  vendorId: string;
  current: LifecycleState;
  onClose: () => void;
  onDone: () => void;
}) {
  const [toState, setToState] = useState<LifecycleState>(current);
  const [reason, setReason] = useState("");

  return (
    <ActionDialog
      open={open}
      title="Change lifecycle state"
      confirmLabel="Record transition"
      disabled={reason.trim() === "" || toState === current}
      onClose={onClose}
      onDone={onDone}
      run={() => vendorApi.transition(orgId, vendorId, toState, reason.trim())}
    >
      <Field label="New state">
        {(id) => (
          <Select id={id} value={toState} onChange={(e) => setToState(e.target.value as LifecycleState)}>
            {LIFECYCLE_STATES.map((s) => (
              <option key={s} value={s}>
                {STATE_LABEL[s]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Reason">
        {(id) => <TextArea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </ActionDialog>
  );
}

function AttachEvidenceDialog({
  open,
  orgId,
  vendorId,
  onClose,
  onDone,
}: {
  open: boolean;
  orgId: string;
  vendorId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [evidenceType, setEvidenceType] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [issuedBy, setIssuedBy] = useState("");

  return (
    <ActionDialog
      open={open}
      title="Attach evidence"
      description="Name a document held in Studio Documents. Its filename, version and checksum are read from Documents with your own access, not typed here."
      confirmLabel="Attach"
      disabled={evidenceType.trim() === "" || documentId.trim() === "" || effectiveFrom === ""}
      onClose={onClose}
      onDone={onDone}
      run={() =>
        vendorApi.attachEvidence(orgId, vendorId, {
          evidenceType: evidenceType.trim(),
          documentId: documentId.trim(),
          effectiveFrom,
          expiresAt: expiresAt || null,
          issuedBy: issuedBy.trim() || null,
        })
      }
    >
      <div className="form-grid">
        <Field label="Evidence type" help="For example: SOC2_TYPE_II, INSURANCE_CERTIFICATE">
          {(id) => <TextInput id={id} value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)} />}
        </Field>
        <Field label="Document ID">
          {(id) => <TextInput id={id} value={documentId} onChange={(e) => setDocumentId(e.target.value)} />}
        </Field>
        <Field label="Effective from">
          {(id) => (
            <TextInput id={id} type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          )}
        </Field>
        <Field label="Expires">
          {(id) => <TextInput id={id} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />}
        </Field>
        <Field label="Issued by">
          {(id) => <TextInput id={id} value={issuedBy} onChange={(e) => setIssuedBy(e.target.value)} />}
        </Field>
      </div>
    </ActionDialog>
  );
}
